import React from 'react';

interface EstateCardProps {
  type: 'own' | 'executor';
  estateName: string;
  ownerEmail?: string;
  status: 'active' | 'pending' | 'approved' | 'no-cert';
  onClick: () => void;
  className?: string;
}

const EstateCard: React.FC<EstateCardProps> = ({
  type,
  estateName,
  ownerEmail,
  status,
  onClick,
  className = ''
}) => {
  const getStatusBadge = () => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5"></span>
            Active
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mr-1.5"></span>
            Awaiting Approval
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
            <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full mr-1.5"></span>
            Full Access
          </span>
        );
      case 'no-cert':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-600">
            <span className="w-1.5 h-1.5 bg-stone-400 rounded-full mr-1.5"></span>
            Death Cert Required
          </span>
        );
      default:
        return null;
    }
  };

  const getIcon = () => {
    if (type === 'own') {
      return (
        <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
          <svg className="w-6 h-6 text-cyan-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </div>
      );
    }
    return (
      <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
        <svg className="w-6 h-6 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
    );
  };

  const getActionText = () => {
    if (type === 'own') {
      return 'Continue Planning →';
    }
    switch (status) {
      case 'approved':
        return 'Begin Settlement →';
      case 'pending':
        return 'View Status →';
      case 'no-cert':
        return 'Upload Death Certificate →';
      default:
        return 'View →';
    }
  };

  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-white rounded-xl shadow-sm border border-stone-200 hover:border-cyan-300 hover:shadow-md transition-all p-5 ${className}`}
    >
      <div className="flex items-start gap-4">
        {getIcon()}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="font-semibold text-stone-800 truncate">
              {type === 'own' ? 'My Estate Plan' : estateName}
            </h3>
            {getStatusBadge()}
          </div>
          {type === 'executor' && ownerEmail && (
            <p className="text-sm text-stone-500 truncate">{ownerEmail}</p>
          )}
          <p className="text-sm text-cyan-700 font-medium mt-2">
            {getActionText()}
          </p>
        </div>
      </div>
    </button>
  );
};

export default EstateCard;

